import { Component, ReactNode } from "react";
import { AlertTriangle, RotateCw, Home, MessageCircle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Глобальный error boundary для всего приложения.
 * Показывает аккуратный UI восстановления вместо белого/чёрного экрана.
 * Логирует падение в Yandex.Metrika как цель app_crash.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info);
    try {
      if (typeof window !== "undefined" && window.ym) {
        window.ym(106989563, "reachGoal", "app_crash", {
          message: error instanceof Error ? error.message : String(error),
          path: window.location.pathname,
        });
      }
    } catch (_e) {
      // ignore
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main
        role="alert"
        aria-live="assertive"
        className="flex min-h-screen items-center justify-center bg-background px-4 py-10"
      >
        <section className="w-full max-w-md rounded-2xl border border-border/40 bg-card/60 p-6 text-center shadow-xl backdrop-blur-md sm:p-8">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-7 w-7 text-destructive" aria-hidden />
          </div>
          <h1 className="mb-2 text-xl font-bold text-foreground sm:text-2xl">
            Что-то пошло не так
          </h1>
          <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
            Сервис временно сбойнул на этом экране. Ваши данные в безопасности — попробуйте перезагрузить страницу или вернуться на главную.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <RotateCw className="h-4 w-4" aria-hidden />
              Перезагрузить
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-border/60 bg-background/40 px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-background/60"
            >
              <Home className="h-4 w-4" aria-hidden />
              На главную
            </button>
          </div>
          <a
            href="/contacts"
            className="mt-5 inline-flex items-center justify-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <MessageCircle className="h-3.5 w-3.5" aria-hidden />
            Написать в поддержку
          </a>
          {this.state.error?.message && (
            <details className="mt-5 rounded-lg border border-border/30 bg-background/30 p-3 text-left text-[11px] text-muted-foreground">
              <summary className="cursor-pointer select-none">Технические детали</summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words">{this.state.error.message}</pre>
            </details>
          )}
        </section>
      </main>
    );
  }
}

declare global {
  interface Window {
    ym?: (counterId: number, method: string, goal: string, params?: Record<string, unknown>) => void;
  }
}
